<template>
    <div>
        <div v-if="uuid && !isLoading">
            <div class="columns">
                <div class="column content">
                    <h2>{{ get('title') }}</h2>

                    <ul class="has-text-weight-bold quiz-activity-settings">
                        <li v-if="head(remediations) && head(remediations).quiz_time_limit">
                            <span>
                                Time Limit: {{ minutes(head(remediations).quiz_time_limit) }}
                                {{ pluralize('minute', minutes(head(remediations).quiz_time_limit)) }}

                                <span v-if="head(remediations).quiz_time_limit % 60 !== 0">
                                    {{ remainderSeconds(head(remediations).quiz_time_limit) }}
                                    {{ pluralize('second', remainderSeconds(head(remediations).quiz_time_limit)) }}
                                </span>
                            </span>
                        </li>

                        <li v-else-if="userHasAuthorizedAttempt && userUnusedAuthorizedAttempts[0].quiz_time_limit">
                            <e-icon icon="stopwatch" />

                            <span v-if="userHasAuthorizedAttempt && userUnusedAuthorizedAttempts[0].quiz_time_limit">
                                Time Limit: {{ minutes(userUnusedAuthorizedAttempts[0].quiz_time_limit) }}
                                {{ pluralize('minute', minutes(userUnusedAuthorizedAttempts[0].quiz_time_limit)) }}

                                <span v-if="userUnusedAuthorizedAttempts[0].quiz_time_limit % 60 !== 0">
                                    {{ remainderSeconds(userUnusedAuthorizedAttempts[0].quiz_time_limit) }}
                                    {{
                                        pluralize(
                                            'second',
                                            remainderSeconds(userUnusedAuthorizedAttempts[0].quiz_time_limit)
                                        )
                                    }}
                                </span>
                            </span>
                        </li>

                        <li v-else-if="!isNull(content.time_limit)">
                            <span>
                                Time Limit: {{ minutes(content.time_limit) }}
                                {{ pluralize('minute', minutes(content.time_limit)) }}

                                <span v-if="content.time_limit % 60 !== 0">
                                    {{ remainderSeconds(content.time_limit) }}
                                    {{ pluralize('second', remainderSeconds(content.time_limit)) }}
                                </span>
                            </span>
                        </li>

                        <li v-else>
                            <e-icon icon="stopwatch-slash" />

                            <span>No Time Limit</span>
                        </li>

                        <li>
                            <e-icon icon="check-circle" />

                            <span>Total Questions: {{ questionCount }}</span>
                        </li>

                        <li v-if="content.lockdown">
                            <e-icon icon="lock" />

                            <span>Navigation Locked</span>

                            <e-tooltip
                                class="m-l-sm icon-trigger"
                                :label="translate('quiz_attempt_lockdown', { type })"
                                multilined
                                position="is-right"
                            >
                                <e-icon icon="info-circle" class="has-text-info" />
                            </e-tooltip>
                        </li>

                        <li>
                            <e-icon icon="book-reader" />

                            <span>{{ content.review_allowed ? 'Review Available' : 'Review Not Available' }}</span>
                        </li>

                        <li v-if="content.password_enabled">
                            <e-icon icon="key-skeleton" />

                            <span>Password Required</span>

                            <e-tooltip
                                class="m-l-sm icon-trigger"
                                :label="translate('quiz_attempt_password')"
                                multilined
                                position="is-right"
                            >
                                <e-icon icon="info-circle" class="has-text-info" />
                            </e-tooltip>
                        </li>

                        <li v-if="content.force_start">
                            <e-icon icon="house-user" />

                            <span>Instructor-Led</span>

                            <e-tooltip
                                class="m-l-sm icon-trigger"
                                :label="translate('quiz_attempt_force_start')"
                                multilined
                                position="is-right"
                            >
                                <e-icon icon="info-circle" class="has-text-info" />
                            </e-tooltip>
                        </li>

                        <li v-if="content.calculator_enabled">
                            <e-icon icon="calculator" />

                            <span>Calculator Available</span>
                        </li>

                        <li v-if="computedActivity.activity_settings.course_authorized_role_id > 0">
                            <e-icon icon="eye-slash" />

                            <span>
                                {{
                                    permissionBasedActivity(
                                        computedActivity.activity_settings.course_authorized_role_id
                                    )
                                }}
                            </span>
                        </li>
                    </ul>
                </div>

                <div v-if="!readOnly && canSeeAdministrationDropdown" class="column">
                    <div class="is-title-button-group">
                        <div>
                            <e-dropdown id="quiz-admin-tools" position="is-bottom-left" :mobile-modal="!$isTablet">
                                <template #trigger>
                                    <e-button type="is-primary" icon-right="angle-down" outlined>
                                        {{ type === 'survey' ? 'Survey' : 'Quiz' }} Tools
                                    </e-button>
                                </template>

                                <e-dropdown-item @click="isComponentModalActive = true"> Preview </e-dropdown-item>

                                <e-dropdown-item
                                    v-if="!content.force_start"
                                    :disabled="content.force_start"
                                    @click="previewQuiz"
                                >
                                    Student View
                                </e-dropdown-item>

                                <e-dropdown-item
                                    @click="
                                        $router.push({
                                            path: `/quizzes/${quizId}?course_ids=${routeProps.courseId}`,
                                        })
                                    "
                                >
                                    Reports
                                </e-dropdown-item>
                            </e-dropdown>
                            <e-modal v-model="isComponentModalActive" has-modal-card trap-focus :can-cancel="false">
                                <quick-quiz-view :id="quizId" :activity="activityProp" @close="closeModal" />
                            </e-modal>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="quizDescription" class="columns">
                <div class="column content">
                    <display-content :content="quizDescription" />
                </div>
            </div>

            <div v-if="!readOnly" class="columns">
                <div class="column">
                    <div class="field" :class="$isMobile ? '' : 'is-grouped is-grouped-right'">
                        <div v-if="!content.force_start" class="field control">
                            <e-tooltip position="is-bottom" :active="calculateDisabledReasons().length > 0">
                                <e-button
                                    v-if="!attemptInProgress && !attemptId"
                                    :class="{ 'is-loading': launchQuizLoading }"
                                    :disabled="calculateDisabledReasons().length > 0"
                                    type="is-primary"
                                    @click="launchQuiz(false)"
                                    @keyup="launchQuiz(false)"
                                >
                                    {{
                                        type === 'survey' ? translate('begin_survey') : translate('begin_quiz_attempt')
                                    }}
                                </e-button>

                                <template #content>
                                    This quiz is disabled for the following reasons:
                                    <ol style="padding-left: 2rem">
                                        <li v-for="(reason, index) in calculateDisabledReasons()" :key="index">
                                            {{ reason }}
                                        </li>
                                    </ol>
                                </template>

                                <e-button v-if="attemptInProgress && !attemptId" type="is-primary" disabled>
                                    {{ quizAttemptType ? translate(`${quizAttemptType} already in progress`) : '' }}
                                </e-button>
                            </e-tooltip>

                            <e-button v-if="attemptId" type="is-primary" @click="resumeQuiz">
                                Resume {{ type === 'survey' ? 'Survey' : 'Attempt' }}
                            </e-button>
                        </div>

                        <div
                            v-if="content.force_start && $gate.allows('createOther', ['QuizAttempt', course])"
                            class="field control"
                        >
                            <e-button type="is-primary" @click="launchQuizEnrollment">
                                {{ translate('manage_quiz_attempts') }}
                            </e-button>
                        </div>
                        <div
                            v-if="content.force_start && authUser.isStudentOf(routeProps.courseId)"
                            class="is-flex is-flex-centered"
                        >
                            <e-tooltip
                                v-if="maxReached || userGrades.length >= 1"
                                class="mr-3"
                                :label="checkInInfoText"
                                multilined
                            >
                                <e-icon type="is-primary" icon="question-circle" />
                            </e-tooltip>

                            <e-tooltip
                                multilined
                                :label="checkedIn ? 'You are checked in. An instructor will begin your quiz soon.' : ''"
                                :active="checkedIn"
                            >
                                <e-button
                                    :outlined="checkedIn"
                                    :disabled="checkedIn || !canCheckInToQuiz || maxReached"
                                    :type="checkedIn ? 'is-success' : 'is-primary'"
                                    :label="checkedIn ? 'Checked Into Quiz' : 'Check Into Quiz'"
                                    @click="checkIntoManagedQuiz(true)"
                                    @keyup="checkIntoManagedQuiz(true)"
                                />
                            </e-tooltip>

                            <e-tooltip v-if="checkedIn" label="Clear check in.">
                                <e-button
                                    v-if="checkedIn"
                                    type="is-primary"
                                    outlined
                                    icon-right="times"
                                    class="ml-3"
                                    @click="toggleCheckInStatus(false)"
                                    @keyup="toggleCheckInStatus(false)"
                                />
                            </e-tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!readOnly" class="columns">
                <div class="column">
                    <hr />
                </div>
            </div>

            <div
                v-if="!readOnly && canReview && $gate.allows('viewAnyWithinCourse', ['QuizAttempt', course])"
                class="columns"
            >
                <div class="column">
                    <submissions
                        :namespace="namespace"
                        :provide-submissions="submissions"
                        @fetch-submissions="fetchSubmissions"
                    >
                        <template #content="{ submission }">
                            <div v-if="quizAttempt" class="content has-text-weight-bold has-text-centered-touch">
                                <div>
                                    {{ submission.submittable.quiz_attempt.points_earned }}
                                    /
                                    {{ submission.submittable.quiz_attempt.points_possible }}
                                </div>

                                <div>
                                    {{ translate('score') }}:
                                    {{ numeral(submission.submittable.quiz_attempt.total_score).format('0.0%') }}
                                </div>
                            </div>
                        </template>
                    </submissions>
                </div>
            </div>
        </div>

        <e-loading v-if="isLoading" v-model:active="isLoading" is-full-page />

        <e-modal v-model="isQuizInProgressModalActive" has-modal-card>
            <quiz-in-progress-modal
                :quiz-attempt="head(attemptInProgressModel)"
                :current-type="type"
                @close="isQuizInProgressModalActive = false"
            />
        </e-modal>

        <e-modal v-model="isLaunchQuizModalActive" class="tw:w-3/4" :can-cancel="false" has-modal-card trap-focus>
            <launch-quiz
                :activity="activityProp"
                :uuid="uuid"
                :type="type"
                :preview="isPreviewing"
                :submission-count="submissionCount"
                @close="closeLaunchedQuiz"
            />
        </e-modal>
    </div>
</template>

<script>
import capitalize from 'lodash/capitalize.js';
import find from 'lodash/find.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import head from 'lodash/head.js';
import isEmpty from 'lodash/isEmpty.js';
import isNull from 'lodash/isNull.js';
import values from 'lodash/values.js';
import numeral from 'numeral';
import pluralize from 'pluralize';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';

import LaunchQuiz from '@/components/activity-types/course-activity-types/activities/quiz/LaunchQuiz.vue';
import QuickQuizView from '@/components/activity-types/course-activity-types/activities/quiz/QuickQuizView.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import QuizInProgressModal from '@/components/quizzes/QuizInProgressModal.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import GroupPermissionMixin from '@/mixins/GroupPermission.mixin';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import Remediation from '@/mixins/Remediation.mixin.js';
import Course from '@/models/Course.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    name: 'CourseQuiz',

    components: {
        QuizInProgressModal,
        QuickQuizView,
        DisplayContent,
        Submissions,
        LaunchQuiz,
    },

    mixins: [GroupPermissionMixin, IsLateMixin, AttemptAuthorizationMixin, Remediation],

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        routeProps: {
            type: Object,
            default: null,
        },

        type: {
            type: String,
            default: null,
        },

        activityProp: {
            type: Object,
            default: null,
        },
    },

    data: () => ({
        launchQuizLoading: false,
        isLaunchQuizModalActive: false,
        isQuizInProgressModalActive: false,
        isPreviewing: false,
        isLoading: false,
        attemptId: null,
        attemptInProgress: true,
        attemptInProgressModel: null,
        quizAttemptType: null,
        disabledReason: [],
        isComponentModalActive: false,
        checkedIn: false,
        actualAttemptCount: null,
        submissions: [],
    }),

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        activityId() {
            return Number(this.routeProps.activityId);
        },

        course() {
            return this.readOnly
                ? null
                : Course.query().whereId(parseInt(this.routeProps.courseId, 10)).with('groups').first();
        },

        computedActivity() {
            if (this.readOnly) {
                return this.activityProp;
            }
            return this.$store.getters['Activities/getByKey']({
                keyName: 'id',
                key: this.activityId,
            });
        },

        activity() {
            return this.computedActivity;
        },

        surveyTitle() {
            return this.getActivityField('title') || this.translate('survey');
        },

        submissionCount() {
            // Use actual attempt count if we've fetched it
            if (this.actualAttemptCount !== null) {
                return this.actualAttemptCount;
            }

            // Fallback to activity field (which might be stale)
            return this.getActivityField('user_submission_count') || 0;
        },

        emptySubmissionsPlaceholder() {
            // This is for the submissions component - it expects quiz submissions
            return [];
        },

        canSeeAdministrationDropdown() {
            if (this.readOnly) return false;
            return this.userHasAccessToQuizAdminTools(this.routeProps.courseId);
        },

        quizDescription() {
            return this.getActivityField('description');
        },

        canSubmitLate() {
            const settings = get(this.computedActivity, 'activity_settings');
            const canSubmitLate = get(settings, 'allow_submissions_after_due_date');

            if (canSubmitLate) return true;

            return !this.isPastDue || this.userHasAuthorizedAttempt;
        },

        content() {
            return this.getActivityField('activity_content');
        },

        currentAttempts() {
            if (this.canSeeAdministrationDropdown) {
                return this.get('quiz_attempts').filter(preview => !preview.is_preview).length;
            }
            return this.get('quiz_attempts').length;
        },

        hasSubmissions() {
            return this.submissionCount > 0;
        },

        maxAttempts() {
            return this.getActivityField('activity_settings').max_attempts_allowed;
        },

        traditionalAttemptsRemaining() {
            if (this.isLate && !this.computedActivity.activity_settings.allow_submissions_after_due_date) {
                return 0;
            }

            return Math.max(0, this.computedActivity.activity_settings.max_attempts_allowed - this.submissionCount);
        },

        attemptsRemaining() {
            return this.traditionalAttemptsRemaining + this.authorizedAttemptsRemaining;
        },

        maxReached() {
            return this.attemptsRemaining <= 0;
        },

        questionCount() {
            return this.computedActivity?.question_count ?? 'N/A';
        },

        quizActivityId() {
            return this.getActivityField('contentable_id');
        },

        quizId() {
            return this.content.quiz_id;
        },

        quizSettings() {
            return this.computedActivity.activity_settings;
        },

        title() {
            return this.computedActivity.title;
        },

        totalPoints() {
            // TODO: Get total points from back end

            return null;
        },

        gradeLink() {
            return {
                name: 'student-grade-view',
                params: {
                    courseId: this.$route.params.courseId,
                    userId: this.$store.getters['Auth/userId'],
                },
            };
        },

        userGrades() {
            return values(this.$store.getters['GradebookGrades/all']()).filter(
                grade => grade.activity_id === this.activityId && grade.user_id === this.$store.getters['Auth/userId']
            );
        },

        enrollmentPermissions() {
            return this.$store.getters['ActiveEnrollment/permissions'];
        },

        canGradeQuizzes() {
            return !!find(
                this.enrollmentPermissions,
                permission => permission.subject === "another user's grade" && permission.action === 'create'
            );
        },

        canReview() {
            return this.quizSettings.review_allowed || this.canGradeQuizzes || this.type === 'survey';
        },

        isPastDue() {
            let dueDate = get(this.activity, 'activity_settings.due_at');

            if (!dueDate) return false;

            dueDate = this.$moment.utc(dueDate).tz(this.$timezone.name);

            return !this.$moment().isBefore(dueDate);
        },

        checkInInfoText() {
            if (this.userGrades.length > 0)
                return 'You cannot check into this quiz because you have received a grade for this activity.';

            if (this.maxReached) return 'You cannot check into this quiz because you have no remaining attempts.';

            return null;
        },

        quizAttempt() {
            const attempt = this.submission?.submittable?.quiz_attempt;

            return attempt && !this.isNull(attempt) ? attempt : null;
        },

        canCheckInToQuiz() {
            if (this.userHasAuthorizedAttempt) {
                return true;
            }
            if (this.userGrades.length === 0 && !this.maxReached) {
                return true;
            }
            return false;
        },
    },

    watch: {
        // Watch for changes to the quizActivityId route parameter to refetch attempt count
        '$route.params.quizActivityId': function (newQuizActivityId, oldQuizActivityId) {
            if (!this.readOnly && newQuizActivityId !== oldQuizActivityId) {
                this.fetchActualAttemptCount();
            }
        },

        // Watch for activity ID changes
        activityId() {
            if (!this.readOnly) {
                this.fetchActualAttemptCount();
            }
        },
    },

    mounted() {
        if (!this.readOnly) {
            this.verifyActivity();
        }
    },

    activated() {
        // Called when component is activated from keep-alive cache
        if (!this.readOnly) {
            this.fetchActualAttemptCount();
        }
    },

    async created() {
        await this.fetchActivityData();

        // Fetch actual attempt count for accurate calculation
        await this.fetchActualAttemptCount();

        const type = this.type ? this.type : 'quiz';

        const { data } = await axios.get(`/api/quiz-attempts?filter[active_${type}_attempt]=${this.quizActivityId}`);

        if (data.data.length > 0) {
            this.attemptId = data.data[0].id;
        }

        this.checkedIn = this.authUser.quiz_checkin_id > 0;

        await this.checkForInProgressAttempts();
    },

    methods: {
        capitalize,
        numeral,
        isEmpty,
        isNull,
        head,
        has,
        pluralize,

        async fetchActivityData() {
            try {
                const response = await axios.get(`/api/activities/${this.activityId}?append=question_count`);
                // Store the question count in the activity data
                if (response.data?.data) {
                    await this.$store.dispatch('Activities/hydrateActivity', {
                        payload: response.data.data,
                    });
                }
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async fetchActualAttemptCount() {
            try {
                const type = this.type || 'quiz';
                const userId = this.$store.getters['Auth/userId'];

                // Fetch all quiz attempts for this user and quiz activity
                const response = await axios.get(
                    `/api/quiz-attempts?filter[user_id]=${userId}&filter[${type}_activity_id]=${this.quizActivityId}`
                );

                if (response.data?.data) {
                    // Count non-preview attempts
                    this.actualAttemptCount = response.data.data.filter(attempt => !attempt.is_preview).length;
                } else {
                    this.actualAttemptCount = 0;
                }
            } catch (error) {
                console.error('Error fetching attempt count:', error);
                this.actualAttemptCount = 0;
            }
        },

        async fetchSubmissions(toggleLoading) {
            if (this.$gate.allows('viewAnyWithinCourse', ['QuizAttempt', this.course])) {
                this.submissions = await this.$store.dispatch(`QuizAttempt/${this.quizId}/fetchQuizSubmissions`, {
                    activityId: this.activityId,
                    userId: this.$store.getters['Auth/userId'],
                });

                return toggleLoading();
            }

            return toggleLoading();
        },

        get(field) {
            if (!this.$store.getters[`QuizAttempt/${this.quizId}/get`]) {
                return field === 'title' ? '' : {};
            }

            if (!this.readOnly) {
                return this.$store.getters[`QuizAttempt/${this.quizId}/get`](field);
            }

            return null;
        },

        getActivityField(field) {
            return this.$store.getters[`${this.namespace}/${this.uuid}/get`](field);
        },

        submissionTitle(submission) {
            return submission.created_by === this.$store.getters['Auth/userId'] ? 'Your Attempt' : submission.user.name;
        },

        submissionIsGraded(submission) {
            return find(this.userGrades, userGrade => userGrade.activity_submission_id === submission.id);
        },

        verifyActivity() {
            return this.$store.dispatch('Activities/verifyActivity', {
                course_id: this.$route.params.courseId,
                activity_id: this.computedActivity.id,
            });
        },

        async previewQuiz() {
            await this.launchQuiz(true);
        },

        closeLaunchedQuiz() {
            this.launchQuizLoading = false;
            this.isLaunchQuizModalActive = false;

            // Refresh attempt count when returning from quiz
            this.fetchActualAttemptCount();
        },

        async launchQuiz(preview) {
            this.launchQuizLoading = true;
            this.isPreviewing = preview;
            this.isLaunchQuizModalActive = true;
        },

        async resumeQuiz() {
            await this.startQuiz(this.attemptId);
        },

        async startQuiz(attemptId = null, preview = false) {
            this.toggleLoading();

            await this.$store
                .dispatch('QuizAttempt/launchQuiz', {
                    attemptId,
                    payload: {
                        activityId: this.routeProps.activityId,
                        quizActivityId: this.quizActivityId,
                        courseId: this.routeProps.courseId,
                        quizVersionId: this.quizId,
                        id: this.quizId,
                        preview,
                    },
                    type: this.type,
                    router: this.$router,
                })
                .catch(error => {
                    this.toggleLoading();

                    throw error;
                });
        },

        toggleLoading() {
            this.isLoading = !this.isLoading;
        },

        async checkForInProgressAttempts() {
            const quizAttemptRoute = new QueryBuilder('quiz-attempts')
                .filter('in_progress_quiz_attempt', 0)
                .include('quizActivity.activity')
                .include('surveyActivity.activity');

            this.attemptInProgressModel = await axios.get(quizAttemptRoute.toUrl()).then(resp => resp.data.data);

            if (isEmpty(this.attemptInProgressModel)) {
                this.attemptInProgress = false;
            } else {
                this.attemptInProgress = true;

                this.setData();

                this.launchQuizInProgressModal();
            }
        },

        setData() {
            const quizAttempt = this.attemptInProgressModel;

            if (head(quizAttempt).quiz_activity_id) {
                this.quizAttemptType = 'Quiz';
            }

            if (head(quizAttempt).survey_activity_id) {
                this.quizAttemptType = 'Survey';
            }
        },

        launchQuizInProgressModal() {
            const quizAttempt = this.attemptInProgressModel;

            const quizAttemptActivityId = head(quizAttempt).quiz_activity_id
                ? head(quizAttempt).quiz_activity?.activity?.id
                : head(quizAttempt).survey_activity?.activity?.id;

            if (this.activityId !== quizAttemptActivityId) {
                this.isQuizInProgressModalActive = true;
            }
        },

        launchQuizEnrollment() {
            this.$router.push({
                name: 'manage-quiz-attempts',
                params: {
                    activityId: this.routeProps.activityId,
                    courseId: this.routeProps.courseId,
                },
            });
        },

        minutes(seconds) {
            return Math.floor(seconds / 60);
        },

        remainderSeconds(seconds) {
            return seconds - this.minutes(seconds) * 60;
        },

        permissionBasedActivity(authorizedRoleId) {
            if (authorizedRoleId === 1) {
                return 'Access limited to Course Instructors ';
            }

            if (authorizedRoleId === 2) {
                return 'Access limited to Instructor Within Group ';
            }

            if (authorizedRoleId === 3) {
                return 'Access limited to Instruction Admin ';
            }

            return 'This activity may be restricted to some students and instructors';
        },

        checkIntoManagedQuiz(status) {
            if (this.attemptInProgress) {
                return this.launchQuizInProgressModal(this.quizAttempt);
            }

            this.$dialog.confirm({
                title: 'Confirm Check In',
                message: `
                    <h4 class="mb-3">Policy Regarding Academic Integrity</h4>

                    <p class="mb-3">
                        Any student who engages in academic dishonesty, including, but not limited to, plagiarizing
                        another person's work; cheating on an examination or assignment; distributing copies of
                        examinations, assignments, or answer sheets to other students; passing off another's work as
                        one's own; and/or aiding one or more other students in committing the same or similar acts of
                        academic dishonesty will be given a grade of zero for the exam or assignment in which the
                        infraction occurred. If a student commits a second act of academic dishonesty, he or she will be
                        dismissed from the university. All acts of academic dishonesty will be recorded on a Notice of
                        Deficiency form and placed in the student's permanent academic file. A student may appeal
                        his/her dismissal from the university for academic dishonesty. The appeal must be made in
                        writing to the Office of Instruction.
                    </p>

                    <p>
                        By clicking on <strong>"Confirm Check In"</strong>, I acknowledge that I have read and agree
                        to this policy.
                    </p>
                `,
                confirmText: 'Confirm Check In',
                cancelText: 'Go Back',
                type: 'is-primary',
                size: 'is-small',
                onConfirm: () => this.toggleCheckInStatus(status),
            });

            return undefined;
        },

        async toggleCheckInStatus(status) {
            const requestType = status ? 'post' : 'delete';
            const route = new QueryBuilder(`quiz-check-in${status ? '' : `/${this.authUser.id}`}`);

            await axios[requestType](route.toUrl(), {
                user_id: this.authUser.id,
                activity_id: this.activityId,
            });

            this.$store.commit('User/SET', {
                field: 'quiz_checkin_id',
                value: status ? this.activityId : status,
            });

            this.checkedIn = status;
        },

        closeModal() {
            this.isComponentModalActive = false;
        },

        calculateDisabledReasons() {
            const reasons = [];

            if (!this.userHasAuthorizedAttempt) {
                if (!isEmpty(this.userGrades)) {
                    reasons.push(this.translate('activity_has_been_graded'));
                }

                if (this.maxReached) {
                    reasons.push(this.translate('no_additional_attempts'));
                }

                if (!this.canSubmitLate) {
                    reasons.push(this.translate('no_late_submissions'));
                }
            } else if (this.attemptAuthorizations.length === 0) {
                reasons.push(this.translate('no_attempt_authorizations'));
            }

            if (this.launchQuizLoading) {
                reasons.push('Launching Quiz');
            }

            if (this.attemptInProgress) {
                reasons.push(this.translate('quiz_attempt_in_progress_label'));
            }

            return reasons;
        },
    },
};
</script>

<style lang="scss">
.quiz-activity-settings {
    list-style: none;

    & > li:first-child > span > i {
        font-size: 2rem !important;
    }

    & > li {
        display: flex;
        align-items: center;

        & > span:nth-child(2) {
            margin-left: 0.75rem;
        }
    }
}
</style>

<style lang="css" scoped>
.icon-trigger :deep(.tooltip-trigger) {
    line-height: 0;
}
</style>
